import { useEffect, useState } from 'react';
import { Button, Divider, IconButton, Link, Stack, Tooltip, Typography } from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { navigationHelper } from 'NavigationHelpers';
import { useFormContext } from 'FrontRoyalReactHookForm';
import { TextField } from 'FrontRoyalMaterialUiForm';
import { useSiteMetadata } from 'SearchEngineOptimization/useSiteMetadata';
import { useCurrentUser } from 'FrontRoyalAngular';
import { hasAdminAccess } from 'Users';
import ContentItemImagePreview from './ContentItemImagePreview';
import ContentItemImageUpload from './ContentItemImageUpload';
import { buildShareUrl, shareWindowFeatures } from './buildShareUrl';
import SectionHeading from './SectionHeading';
import SeoLockToggle from './SeoLockToggle';
import { initialSeoLocks, useSeoFieldLocks } from './useSeoFieldLocks';
import { type S3AssetLike, type ShareProvider } from './ContentItemEditor.types';

const FACEBOOK_DEBUGGER_URL = 'https://developers.facebook.com/tools/debug/';

// The host the canonical URL sits under. The Angular textarea hard-coded it too.
const CANONICAL_URL_HOST = 'https://app.quantic.edu';

// Wide enough for an intent link button, which is the widest thing in the rail.
const SHARE_RAIL_WIDTH = 280;

const SHARE_PROVIDERS = [
    { provider: 'twitter', label: 'Twitter Intent Link' },
    { provider: 'facebook', label: 'Facebook Intent Link' },
    { provider: 'linkedin', label: 'Linkedin Intent Link' },
] as const;

type Props = {
    itemId: string;

    // 'course' here. It becomes the first segment of the canonical URL.
    urlPrefix: string;
    image: S3AssetLike | null | undefined;
    uploadingImage: boolean;
    onUploadImage: (files: File[]) => void;
    onRemoveImage: () => void;
    onSave: () => void;
    saving: boolean;
};

const COPIED_MESSAGE_MS = 2000;

/*
    How a mirrored field is written. Not dirty: the three effects below run on load as well as on an
    edit, and a course with no canonical URL derives one immediately, because is_content_item.rb
    creates the metadata with the title only. Dirtying here would make the form dirty before the
    admin touches it, and the unsaved-changes warning would fire on the way out. An edit to the
    source field dirties the form on its own.
*/
const MIRROR = { shouldDirty: false };

// The clipboard API needs a secure context. Our dev host is plain HTTP, so the control is not
// offered there rather than offered and broken. Production is HTTPS.
const hasClipboardAccess = () => !!navigator.clipboard && window.isSecureContext;

// Copies the canonical URL with the domain in front of it.
function CopyCanonicalUrlButton({ url }: { url: string }) {
    const [copied, setCopied] = useState(false);

    if (!hasClipboardAccess()) return null;

    async function copy() {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), COPIED_MESSAGE_MS);
    }

    return (
        <Tooltip title={copied ? 'Copied!' : 'Copy the full URL, with the domain'}>
            <IconButton aria-label="Copy the full canonical URL" size="small" onClick={copy}>
                <ContentCopy fontSize="small" />
            </IconButton>
        </Tooltip>
    );
}

/*
    The React version of the Angular entity-metadata-form directive, for the course editor. The
    Angular directive stays where it is for the playlist editor.

    The SEO metadata saves on its own request and takes effect on production immediately, without
    saving or publishing the course. That is why this panel keeps its own Save button.
*/
// oxlint-disable-next-line max-lines-per-function -- one panel of fields with their lock toggles
export default function EntityMetadataForm({
    itemId,
    urlPrefix,
    image,
    uploadingImage,
    onUploadImage,
    onRemoveImage,
    onSave,
    saving,
}: Props) {
    const SiteMetadata = useSiteMetadata();
    const currentUser = useCurrentUser();
    const { watch, setFieldValue } = useFormContext();

    const title = watch('title');
    const description = watch('description');
    const seoTitle = watch('seo_title');
    const seoDescription = watch('seo_description');
    const canonicalUrl = watch('seo_canonical_url');
    const tweetTemplate = watch('tweet_template');

    const derivedCanonicalUrl = SiteMetadata.seoCanonicalUrlFromTitle(urlPrefix, seoTitle, itemId);

    const { locks, toggle } = useSeoFieldLocks(() =>
        initialSeoLocks({
            itemTitle: title,
            itemDescription: description,
            seoTitle,
            seoDescription,
            canonicalUrl,
            derivedCanonicalUrl,
        }),
    );

    // A locked SEO title follows the course title.
    useEffect(() => {
        if (locks.title && seoTitle !== title) setFieldValue('seo_title', title, MIRROR);
    }, [locks.title, title, seoTitle, setFieldValue]);

    useEffect(() => {
        if (locks.description && seoDescription !== description) {
            setFieldValue('seo_description', description, MIRROR);
        }
    }, [locks.description, description, seoDescription, setFieldValue]);

    // A locked canonical URL follows the SEO title, which may itself be following the course title.
    useEffect(() => {
        if (locks.canonicalUrl && canonicalUrl !== derivedCanonicalUrl) {
            setFieldValue('seo_canonical_url', derivedCanonicalUrl, MIRROR);
        }
    }, [locks.canonicalUrl, canonicalUrl, derivedCanonicalUrl, setFieldValue]);

    // The Angular panel was hidden from anyone below admin.
    if (!hasAdminAccess(currentUser)) return null;

    function share(provider: ShareProvider) {
        // The whole metadata group goes, taken from the form rather than the saved record, so the
        // preview shows what the admin just typed.
        const shareInfo = SiteMetadata.contentCompletedShareInfo(currentUser, {
            title,
            description,
            entity_metadata: {
                title: seoTitle,
                description: seoDescription,
                canonical_url: canonicalUrl,
                tweet_template: tweetTemplate,
            },

            // Lesson::Stream carries this on its prototype. A plain record has to say it.
            utmCampaign: 'share_course',
        });

        // The URL is built here rather than through Navigation.ShareService, so this page needs no
        // injector for it. loadUrl is already a plain module.
        navigationHelper().loadUrl(buildShareUrl(provider, shareInfo), '_blank', shareWindowFeatures());
    }

    return (
        // Two regions: everything Save SEO Metadata writes on the left, the read-only share
        // tools in the right rail.
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} sx={{ alignItems: 'stretch' }}>
            <Stack spacing={3} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ alignItems: 'flex-start' }}>
                    {/* With the fields, not in the share rail. The upload writes the file to S3 but
                        does not attach it: the record only gets the image when Save SEO Metadata
                        runs. Out in the rail it looked like it saved itself. */}
                    <Stack spacing={1} sx={{ alignItems: 'center' }}>
                        <ContentItemImagePreview image={image} alt="The SEO image" />

                        <ContentItemImageUpload
                            uploading={uploadingImage}
                            onUpload={onUploadImage}
                            onRemove={image ? onRemoveImage : undefined}
                        />
                    </Stack>

                    <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
                        <TextField
                            name="seo_title"
                            label="SEO Title"
                            fullWidth
                            disabled={locks.title}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <SeoLockToggle
                                            locked={locks.title}
                                            fieldLabel="the SEO title"
                                            onToggle={() => toggle('title')}
                                        />
                                    ),
                                },
                            }}
                        />

                        <TextField
                            name="seo_description"
                            label="SEO Description"
                            fullWidth
                            multiline
                            rows={3}
                            disabled={locks.description}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <SeoLockToggle
                                            locked={locks.description}
                                            fieldLabel="the SEO description"
                                            onToggle={() => toggle('description')}
                                        />
                                    ),
                                },
                            }}
                        />

                        <TextField
                            name="seo_canonical_url"
                            label="Canonical URL"
                            fullWidth
                            disabled={locks.canonicalUrl}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <Stack
                                            direction="row"
                                            sx={{
                                                alignItems: 'center',

                                                // Off the end of the value, which otherwise runs
                                                // into the copy icon.
                                                marginInlineStart: 1,

                                                /*
                                                    The lock comes wrapped in an InputAdornment,
                                                    which carries 8px of its own margin. Halved, so
                                                    the two icons read as one cluster rather than as
                                                    two separate controls.
                                                */
                                                '& .MuiInputAdornment-root': { marginInlineStart: 0.5 },
                                            }}
                                        >
                                            {!!canonicalUrl && (
                                                <CopyCanonicalUrlButton url={`${CANONICAL_URL_HOST}${canonicalUrl}`} />
                                            )}

                                            <SeoLockToggle
                                                locked={locks.canonicalUrl}
                                                fieldLabel="the canonical URL"
                                                onToggle={() => toggle('canonicalUrl')}
                                            />
                                        </Stack>
                                    ),
                                },
                            }}
                        />

                        <TextField
                            name="tweet_template"
                            label="Twitter Template Text"
                            placeholder="Tweet template text"
                            fullWidth
                            multiline
                            rows={2}
                        />
                    </Stack>
                </Stack>

                <Divider />

                {/* A rule above the button, as the save panel has, so the fields above read as the
                    thing it writes rather than as more of the same. */}
                <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
                    <Button
                        data-id="save-metadata"
                        variant="contained"
                        color="success"
                        onClick={onSave}
                        disabled={saving}
                    >
                        Save SEO Metadata
                    </Button>
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                        Updates the metadata on production immediately. This does not save the course.
                    </Typography>
                </Stack>
            </Stack>

            {/* Only once the regions are side by side. Stacked, the spacing already separates them. */}
            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />

            {/* flexShrink, or the fields squeeze the rail below the width of an intent link button. */}
            <Stack spacing={3} sx={{ width: { xs: '100%', md: SHARE_RAIL_WIDTH }, flexShrink: 0 }}>
                <Stack spacing={1}>
                    <SectionHeading>Share Previews</SectionHeading>

                    {SHARE_PROVIDERS.map(({ provider, label }) => (
                        <Button key={provider} variant="outlined" onClick={() => share(provider)}>
                            {label}
                        </Button>
                    ))}

                    <Typography variant="body2">
                        {'Not seeing your updated image or text in the share preview? Load up the '}
                        {/* color="secondary", because a bare anchor picks up the legacy global
                            a:hover colour. */}
                        <Link href={FACEBOOK_DEBUGGER_URL} target="_blank" rel="noreferrer" color="secondary">
                            facebook debugger
                        </Link>
                        {
                            ', enter the url of the course, and click "Fetch new scrape information." Copy the url from the Canonical URL field.'
                        }
                    </Typography>
                </Stack>
            </Stack>
        </Stack>
    );
}
